/**
 * Patine Header — scroll detection, mobile menu toggle,
 * desktop dropdown with GSAP clip-path reveal,
 * mobile accordion with GSAP height animation,
 * mobile overlay with GSAP stagger cascade.
 *
 * Depends on: window.PatineAnimations (GSAP + Lenis)
 * Graceful fallback if PatineAnimations is not loaded.
 */
(() => {
  const init = (root) => {
    const header = root.querySelector('.patine-header');
    if (!header) return;

    const PA = window.PatineAnimations;
    const gsap = PA?.prefersReducedMotion ? null : PA?.gsap;

    // ── Scroll detection via sentinel ──
    const sentinel = document.querySelector('#patine-header-sentinel');
    if (sentinel && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          header.classList.toggle('patine-header--scrolled', !entries[0].isIntersecting);
        },
        { threshold: 0 },
      );
      observer.observe(sentinel);
    }

    // ── Mobile menu toggle ──
    const hamburger = header.querySelector('.patine-header__hamburger');
    const mobileNav = root.querySelector('.patine-header__mobile-nav');
    if (!hamburger || !mobileNav) return;

    const openMobileMenu = () => {
      mobileNav.setAttribute('data-open', 'true');
      hamburger.setAttribute('aria-expanded', 'true');

      if (PA?.lenis) PA.stopScroll();
      else document.body.style.overflow = 'hidden';

      if (gsap) {
        const items = mobileNav.querySelectorAll('.patine-header__mobile-item');
        const cta = mobileNav.querySelector('.patine-btn');
        gsap.from(items, {
          opacity: 0,
          y: 20,
          stagger: 0.06,
          duration: 0.4,
          ease: 'power3.out',
        });
        if (cta) {
          gsap.from(cta, {
            opacity: 0,
            y: 10,
            duration: 0.3,
            ease: 'power2.out',
            delay: items.length * 0.06 + 0.1,
          });
        }
      }
    };

    const closeMobileMenu = () => {
      if (gsap) {
        gsap.to(mobileNav, {
          opacity: 0,
          duration: 0.2,
          ease: 'power2.in',
          onComplete: () => {
            mobileNav.setAttribute('data-open', 'false');
            hamburger.setAttribute('aria-expanded', 'false');
            gsap.set(mobileNav, { opacity: 1 });

            // Reset any open accordions
            const openToggles = mobileNav.querySelectorAll('.patine-header__mobile-toggle[aria-expanded="true"]');
            openToggles.forEach((t) => {
              t.setAttribute('aria-expanded', 'false');
              const sub = t.nextElementSibling;
              if (sub) gsap.set(sub, { height: 0 });
            });

            if (PA?.lenis) PA.startScroll();
            else document.body.style.overflow = '';
          },
        });
      } else {
        mobileNav.setAttribute('data-open', 'false');
        hamburger.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      }
    };

    hamburger.addEventListener('click', () => {
      const isOpen = hamburger.getAttribute('aria-expanded') === 'true';
      if (!isOpen) openMobileMenu();
      else closeMobileMenu();
    });

    // Close button inside mobile overlay
    const closeBtn = mobileNav.querySelector('.patine-header__mobile-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        closeMobileMenu();
      });
    }

    // Close mobile menu on any link click
    mobileNav.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => {
        closeMobileMenu();
      });
    });

    // ── Desktop dropdown — GSAP clip-path reveal + stagger ──
    const dropdowns = root.querySelectorAll('.patine-header__dropdown');
    dropdowns.forEach((details) => {
      const submenu = details.querySelector('.patine-header__submenu');
      const links = submenu ? submenu.querySelectorAll('.patine-header__submenu-link') : [];
      let tl = null;

      const openDropdown = () => {
        details.setAttribute('open', '');
        if (!gsap || !submenu) return;
        if (tl) tl.kill();
        tl = gsap.timeline();
        tl.to(submenu, { clipPath: 'inset(0)', duration: 0.3, ease: 'power2.out' });
        if (links.length) {
          tl.from(links, { opacity: 0, y: 8, stagger: 0.05, duration: 0.25, ease: 'power2.out' }, '-=0.15');
        }
      };

      const closeDropdown = () => {
        if (!gsap || !submenu) {
          details.removeAttribute('open');
          return;
        }
        if (tl) tl.kill();
        gsap.to(submenu, {
          clipPath: 'inset(0 0 100% 0)',
          duration: 0.2,
          ease: 'power2.in',
          onComplete: () => {
            details.removeAttribute('open');
          },
        });
      };

      details.addEventListener('mouseenter', openDropdown);
      details.addEventListener('mouseleave', closeDropdown);
      details.addEventListener('focusout', (e) => {
        if (!details.contains(e.relatedTarget)) closeDropdown();
      });

      // Prevent native toggle from fighting GSAP
      const summary = details.querySelector('summary');
      if (summary) {
        summary.addEventListener('click', (e) => {
          e.preventDefault();
          if (details.hasAttribute('open')) closeDropdown();
          else openDropdown();
        });
      }
    });

    // ── Mobile accordion — exclusive open + GSAP height ──
    const toggles = root.querySelectorAll('.patine-header__mobile-toggle');
    toggles.forEach((btn) => {
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        const submenu = btn.nextElementSibling;

        // Close all other accordions
        if (!expanded) {
          toggles.forEach((other) => {
            if (other !== btn && other.getAttribute('aria-expanded') === 'true') {
              other.setAttribute('aria-expanded', 'false');
              const otherSub = other.nextElementSibling;
              if (otherSub && gsap) {
                gsap.to(otherSub, { height: 0, duration: 0.25, ease: 'power2.inOut' });
              } else if (otherSub) {
                otherSub.style.height = '0';
              }
            }
          });
        }

        btn.setAttribute('aria-expanded', String(!expanded));
        if (!submenu) return;

        if (!expanded) {
          // Open
          submenu.hidden = false;
          if (gsap) {
            gsap.fromTo(submenu, { height: 0 }, { height: 'auto', duration: 0.35, ease: 'power2.inOut' });
            const subLinks = submenu.querySelectorAll('.patine-header__mobile-submenu-link');
            if (subLinks.length) {
              gsap.from(subLinks, {
                opacity: 0,
                y: 10,
                stagger: 0.04,
                duration: 0.3,
                ease: 'power2.out',
                delay: 0.1,
              });
            }
          } else {
            submenu.style.height = 'auto';
          }
        } else {
          // Close
          if (gsap) {
            gsap.to(submenu, {
              height: 0,
              duration: 0.25,
              ease: 'power2.inOut',
              onComplete: () => {
                submenu.hidden = true;
              },
            });
          } else {
            submenu.style.height = '0';
            submenu.hidden = true;
          }
        }
      });
    });
  };

  // Run on load
  init(document);

  // Shopify editor support
  document.addEventListener('shopify:section:load', (e) => {
    init(e.target);
  });
})();
